import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, type LexicalNode } from "lexical";
import { useEffect } from "react";

import { $isProductBlockNode } from "./ProductBlockNode";
import { $isProductContextNode } from "./ProductContextNode";

/** Marks the context/block the caret sits in; the editor styles it like hover. */
export const PRODUCT_ENGAGED_CLASS = "lexical-product-engaged";

/**
 * Mirrors the caret position onto the product wrappers as a CSS class, so the
 * "reveal on engagement" styles fire for keyboard users too — `:hover` covers
 * the pointer, but contenteditable focus lives on the root, so `:focus-within`
 * can't tell which section the caret is in.
 */
export function ProductEngagementPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let engaged: HTMLElement[] = [];
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      const next: HTMLElement[] = [];
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        let node: LexicalNode | null = selection.anchor.getNode();
        while (node !== null) {
          if ($isProductBlockNode(node) || $isProductContextNode(node)) {
            const dom = editor.getElementByKey(node.getKey());
            if (dom) next.push(dom);
          }
          node = node.getParent();
        }
      });
      for (const el of engaged) {
        if (!next.includes(el)) el.classList.remove(PRODUCT_ENGAGED_CLASS);
      }
      for (const el of next) el.classList.add(PRODUCT_ENGAGED_CLASS);
      engaged = next;
    });
    return () => {
      for (const el of engaged) el.classList.remove(PRODUCT_ENGAGED_CLASS);
      unregister();
    };
  }, [editor]);

  return null;
}
